import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  RadioGroup,
} from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SubmitCancelButtons } from "./SubmitCancelButtons";
import type {
  GenericObservationDisplayProps,
  GenericObservationFormProps,
} from "./types";

const formSchema = z.object({
  value: z.string().default("false"),
});

const formatValue = (value?: boolean) => {
  if (value === undefined) {
    return "Unknown";
  }
  return value ? "Yes" : "No";
};

export const GenericObservationYesNoForm = ({
  patientId,
  item,
  itemCategory,
  onSubmit,
  onCancel,
  isSubmitting,
}: GenericObservationFormProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      value: item.documentedObservation?.valueBoolean ? "true" : "false",
    },
  });
  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    const fhirValue = {
      valueBoolean: data.value === "true",
    };
    const payload = {
      patientId: patientId as string,
      category: itemCategory,
      status: "final",
      code: {
        coding: [
          {
            code: item.observationCode,
            system: item.observationCodeSystem,
            display: item.observationCodeDisplay,
          },
        ],
        text: item.observationCodeFriendlyDisplay,
      },
      fhirValue,
    };
    onSubmit(payload);
  };
  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="flex flex-col gap-2">
        <Controller
          control={form.control}
          name="value"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="value">
                <BodyText size="sm" bold>
                  {item.observationCodeFriendlyDisplay}
                </BodyText>
              </FieldLabel>
              <RadioGroup
                {...field}
                id="value"
                onValueChange={(e) => {
                  form.setValue("value", e);
                }}
                options={[
                  {
                    value: "true",
                    label: "Yes",
                  },
                  {
                    value: "false",
                    label: "No",
                  },
                ]}
                aria-invalid={fieldState.invalid}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </div>
    </form>
  );
};

export const GenericObservationYesNoDisplay = ({
  item,
}: GenericObservationDisplayProps) => {
  return (
    <BodyText size="sm">
      {formatValue(item.documentedObservation?.valueBoolean)}
    </BodyText>
  );
};
